/**
 * Helpers for InvoiceCheckout / payment_checkouts on invoice payloads.
 * Checkouts are intents to collect — not payments until paid.
 */

/** @typedef {'pending' | 'paid' | 'cancelled' | 'expired'} CheckoutStatus */
/** @typedef {'shared' | 'separate_group' | 'custom' | 'pay_full'} CheckoutKind */

/**
 * Pending checkouts that still have a gateway URL the user can open.
 * @param {{ payment_checkouts?: Array<{ status?: string, payment_link?: string | null }> } | null | undefined} invoice
 */
export function getPayableCheckouts(invoice) {
  const checkouts = invoice && Array.isArray(invoice.payment_checkouts)
    ? invoice.payment_checkouts
    : [];
  return checkouts.filter((c) => c && c.status === 'pending' && !!c.payment_link);
}

/**
 * Pending checkouts for staff cash / transfer multi-select (link not required).
 * @param {{ payment_checkouts?: Array<{ status?: string }> } | null | undefined} invoice
 */
export function getPendingCheckouts(invoice) {
  const checkouts = invoice && Array.isArray(invoice.payment_checkouts)
    ? invoice.payment_checkouts
    : [];
  return checkouts.filter((c) => c && c.status === 'pending');
}

/**
 * Display title for a checkout (staff + customer).
 * @param {{ kind?: string, settlement_group_name?: string | null, settlement_group_name_ar?: string | null, note?: string | null, amount?: string | number }} checkout
 * @param {'en' | 'ar' | string} locale
 * @param {(key: string, opts?: object) => string} [t] i18n t from ui namespace
 */
export function getCheckoutTitle(checkout, locale, t) {
  if (!checkout) return '';
  const isAr = String(locale || '').toLowerCase().startsWith('ar');
  const translate = typeof t === 'function' ? t : null;

  if (checkout.kind === 'separate_group') {
    if (isAr) {
      return (
        checkout.settlement_group_name_ar
        || checkout.settlement_group_name
        || checkout.note
        || String(checkout.amount ?? '')
      );
    }
    return (
      checkout.settlement_group_name
      || checkout.note
      || String(checkout.amount ?? '')
    );
  }
  if (checkout.kind === 'pay_full') {
    return translate
      ? translate('invoiceForm.checkoutKind.pay_full')
      : (isAr ? 'دفع كامل' : 'Pay full');
  }
  if (checkout.kind === 'custom') {
    return (
      checkout.note
      || (translate
        ? translate('invoiceForm.checkoutKind.custom')
        : (isAr ? 'رابط مخصص' : 'Custom'))
    );
  }
  // shared (default) and unknown
  return translate
    ? translate('invoiceForm.checkoutKind.shared')
    : (isAr ? 'الرسوم المشتركة' : 'Shared charges');
}

/**
 * Sum selected checkout amounts to 3 decimal places (cash picker).
 * @param {number[]} ids
 * @param {Array<{ id?: number, amount?: string | number }>} checkouts
 */
export function sumCheckoutAmounts(ids, checkouts) {
  const idSet = new Set((ids || []).map((id) => Number(id)));
  const total = (checkouts || [])
    .filter((c) => c && idSet.has(Number(c.id)))
    .reduce((acc, c) => acc + Number(c.amount || 0), 0);
  return total.toFixed(3);
}

/**
 * Max amount staff can put on a new checkout:
 * amount_due − sum(other pending checkout amounts).
 * @param {{ amount_due?: string | number, payment_checkouts?: Array<{ status?: string, amount?: string | number }> } | null | undefined} invoice
 */
export function getCheckoutRemainingCapacity(invoice) {
  const amountDue = Number(invoice && invoice.amount_due != null ? invoice.amount_due : 0);
  const checkouts = invoice && Array.isArray(invoice.payment_checkouts)
    ? invoice.payment_checkouts
    : [];
  const pendingReserved = checkouts
    .filter((c) => c && c.status === 'pending')
    .reduce((sum, c) => sum + Number(c.amount || 0), 0);
  return Math.max(0, amountDue - pendingReserved);
}

/**
 * True when the invoice should hide Pay actions.
 * @param {{ paid?: boolean, payment_status?: string, amount_due?: string | number } | null | undefined} invoice
 */
export function isInvoiceFullyPaid(invoice) {
  if (!invoice) return true;
  if (invoice.paid === true) return true;
  if (invoice.payment_status === 'paid') return true;
  if (invoice.amount_due != null && Number(invoice.amount_due) === 0) return true;
  return false;
}

/**
 * Prefer the only pending checkout link; otherwise legacy shared `payment_link`.
 * Does not pick a separate-group URL when `payment_link` is null and multiple
 * checkouts exist (WhatsApp / legacy CTA = shared only).
 * Used when payable.length <= 1 (default single-Pay UX).
 * @param {{ payment_link?: string | null, payment_checkouts?: Array } | null | undefined} invoice
 */
export function getPrimaryPaymentLink(invoice) {
  if (!invoice) return null;
  const payable = getPayableCheckouts(invoice);
  if (payable.length === 1 && payable[0].payment_link) {
    return String(payable[0].payment_link).trim() || null;
  }
  if (invoice.payment_link != null && String(invoice.payment_link).trim()) {
    return String(invoice.payment_link).trim();
  }
  return null;
}

/**
 * Prefer API `message`, then common DRF shapes.
 * @param {unknown} error
 * @param {string} [fallback]
 */
export function getCheckoutApiErrorMessage(error, fallback) {
  const data = error && error.response && error.response.data;
  if (!data) return fallback || '';
  if (typeof data.message === 'string' && data.message) return data.message;
  if (typeof data.error === 'string' && data.error) return data.error;
  if (typeof data.detail === 'string' && data.detail) return data.detail;
  if (typeof data.amount === 'string' && data.amount) return data.amount;
  if (Array.isArray(data.amount) && data.amount[0]) return String(data.amount[0]);
  return fallback || '';
}
